import React, { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'

type Props = {
  value: string[]
  onChange: (value: string[]) => void
  suggestions?: readonly string[]
  placeholder?: string
  disabled?: boolean
}

const MAX_SUGGESTIONS = 8

export const TagInput: React.FC<Props> = ({ value, onChange, suggestions = [], placeholder, disabled = false }) => {
  const { currentTheme: t } = useTheme()
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const query = inputValue.trim().toLowerCase()
  const filtered = suggestions
    .filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()))
    .filter((s) => !query || s.toLowerCase().includes(query))
    .slice(0, MAX_SUGGESTIONS)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setActiveIndex(0)
  }, [inputValue, open])

  const addTag = (raw: string) => {
    const tag = raw.trim()
    if (!tag) return
    if (!value.some((v) => v.toLowerCase() === tag.toLowerCase())) {
      onChange([...value, tag])
    }
    setInputValue('')
    setOpen(false)
  }

  const removeTag = (tag: string) => {
    onChange(value.filter((v) => v !== tag))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (open && filtered.length > 0 && filtered[activeIndex]) {
        addTag(filtered[activeIndex])
      } else if (inputValue.trim()) {
        addTag(inputValue)
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1])
    } else if (e.key === 'ArrowDown' && open && filtered.length > 0) {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp' && open && filtered.length > 0) {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex flex-wrap items-center gap-1.5 w-full min-h-[42px] rounded-xl border px-3 py-1.5 transition ${disabled ? 'cursor-default' : 'cursor-text'}`}
        style={{ backgroundColor: t.inputBackground, borderColor: t.border }}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="flex items-center gap-1 rounded-full pl-2.5 pr-1.5 py-1 text-xs font-medium"
            style={{ backgroundColor: `${t.accent}18`, color: t.accent }}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  removeTag(tag)
                }}
                className="rounded-full p-0.5 transition hover:opacity-70"
                aria-label={`Remove ${tag}`}
              >
                <X size={11} />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => addTag(inputValue)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={value.length === 0 ? (disabled ? '—' : placeholder) : ''}
          className="flex-1 min-w-[120px] bg-transparent text-sm outline-none py-1"
          style={{ color: t.text }}
        />
      </div>
      {open && filtered.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1.5 z-20 max-h-56 overflow-y-auto custom-scrollbar rounded-xl py-1"
          style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}
        >
          {filtered.map((s, i) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addTag(s)}
              onMouseEnter={() => setActiveIndex(i)}
              className="flex items-center w-full px-3.5 py-2 text-left text-sm transition-colors"
              style={{ backgroundColor: i === activeIndex ? t.hoverBackground : 'transparent', color: t.text }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default React.memo(TagInput)
